import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import html2pdf from 'html2pdf.js';
import { X, Printer, Download, CheckCircle, AlertCircle } from 'lucide-react';
import './PrescriptionViewer.css'; 

function CertificateViewer({ certificate, onClose }) {
    const paperRef = useRef(null);
    const [clinic, setClinic] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        async function loadData() {
            if (!certificate) return;

            // Fetch clinic details
            if (certificate.clinic_id) {
                const { data: clinicData } = await supabase
                    .from('clinics')
                    .select('*')
                    .eq('id', certificate.clinic_id)
                    .single();
                if (clinicData) setClinic(clinicData);
            }

            // Fetch doctor details
            if (certificate.doctor_id) {
                const { data: doctorData } = await supabase
                    .from('users')
                    .select('name, signature_url, qualification, specialization, registration_number')
                    .eq('id', certificate.doctor_id)
                    .single();
                if (doctorData) setDoctor(doctorData);
            }
        }
        loadData();
    }, [certificate]);

    // Auto-scaling logic to fit A4 on screen (PrescriptionViewer Pattern)
    useEffect(() => {
        const updateScale = () => {
            if (!paperRef.current) return;

            const isMobile = window.innerWidth <= 768;
            
            if (isMobile) {
                const targetWidth = window.innerWidth - 20;
                const currentWidth = 800; 
                const newScale = targetWidth / currentWidth;
                setScale(newScale);
            } else {
                const targetHeight = window.innerHeight - 150;
                const currentHeight = paperRef.current.scrollHeight || 1122;

                if (currentHeight > targetHeight) {
                    const newScale = targetHeight / currentHeight;
                    setScale(newScale);
                } else {
                    setScale(1);
                }
            }
        };

        if (clinic && doctor) {
            const timer = setTimeout(updateScale, 100);
            window.addEventListener('resize', updateScale);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('resize', updateScale);
            };
        }
    }, [clinic, doctor]);

    const handleDownload = () => {
        const el = paperRef.current;
        if (!el) return;
        
        // Temporarily apply A4 sizing via DOM class (avoids React re-render race)
        el.classList.add('mode-download');
        el.style.transform = 'none';
        
        setTimeout(() => {
            const opt = {
                margin: 0,

                filename: `Fitness_Certificate_${certificate.patient_name.replace(/\s+/g, '_')}_${certificate.certificate_number}.pdf`,
                image: { type: 'jpeg', quality: 1.0 },
                html2canvas: { 
                    scale: 3, 
                    useCORS: true, 
                    logging: false, 
                    letterRendering: true
                },


                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };




            
            // PROGRAMMATIC PAGE CLEANER: Explicitly deletes any extra pages generated by the engine
            html2pdf().set(opt).from(el).toPdf().get('pdf').then((pdf) => {
                const totalPages = pdf.internal.getNumberOfPages();
                if (totalPages > 1) {
                    for (let i = totalPages; i > 1; i--) {
                        pdf.deletePage(i);
                    }
                }
            }).save().then(() => {

                el.classList.remove('mode-download');
                el.style.transform = '';
            });
        }, 100);
    };

    if (!clinic || !doctor) return null;

    const themeColor = clinic.theme_color || '#3b82f6';
    
    // Parse findings if stored as JSON string
    let findingsList = [];
    try {
        if (certificate.selected_findings) {
            findingsList = typeof certificate.selected_findings === 'string' 
                ? JSON.parse(certificate.selected_findings) 
                : certificate.selected_findings;
        }
    } catch (e) {
        console.error("Error parsing findings:", e);
    }

    return (
        <div className="prescription-viewer-overlay" onClick={onClose}>
            <div className="prescription-viewer-actions no-print">
                <button className="rx-action-btn" onClick={() => window.print()}>
                    <Printer size={18} /> Print
                </button>
                <button className="rx-action-btn primary" onClick={handleDownload}>
                    <Download size={18} /> Download PDF
                </button>
                <button className="rx-btn-close-view" onClick={onClose}>
                    <X size={20} />
                </button>
            </div>

            <div 
                ref={paperRef}
                className="prescription-paper"
                id="certificate-pdf-content"
                style={{ 
                    '--theme-color': themeColor,
                    transform: scale !== 1 ? `scale(${scale})` : 'none',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className="rx-header">
                    <div className="rx-header-left">
                        {clinic.logo_url && <img src={clinic.logo_url} alt="Clinic Logo" className="rx-logo" />}
                        <div className="rx-clinic-info">
                            <h1 className="rx-clinic-name">{clinic.name}</h1>
                            <p className="rx-clinic-address">{clinic.address}</p>
                            <div className="rx-clinic-contact">
                                <span>Phone: {clinic.phone}</span>
                                {clinic.email && <span>Email: {clinic.email}</span>}
                            </div>
                        </div>
                    </div>
                    <div className="rx-header-right">
                        <h2 className="rx-doctor-name">Dr. {doctor.name}</h2>
                        <p className="rx-doctor-qual">{doctor.qualification}</p>
                        {doctor.registration_number && <p className="rx-doctor-reg">Reg No: {doctor.registration_number}</p>}
                    </div>
                </div>

                <hr className="rx-divider rx-divider-header" />

                {/* CERTIFICATE TITLE */}
                <div style={{ textAlign: 'center', margin: '20px 0 16px 0' }}>
                    <h2 style={{ 
                        fontSize: '20px', 
                        fontWeight: '800', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.05em',
                        color: '#111827',
                        margin: '0 0 5px 0'
                    }}>
                        Medical Fitness Certificate
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '11px', margin: 0 }}>
                        Certificate No: <strong>{certificate.certificate_number}</strong> | Date: {new Date(certificate.created_at).toLocaleDateString()}
                    </p>
                </div>

                {/* PATIENT INFO BOX */}
                <div className="rx-patient-info" style={{ marginBottom: '14px' }}>
                    <div className="rx-patient-row">
                        <span><strong>Patient Name:</strong> {certificate.patient_name}</span>
                        <span><strong>Age/Gender:</strong> {certificate.patient_age}Y / {certificate.patient_gender}</span>
                    </div>
                    {certificate.patient_phone && (
                        <div className="rx-patient-row">
                            <span><strong>Contact:</strong> {certificate.patient_phone}</span>
                        </div>
                    )}
                </div>

                {/* BODY CONTENT */}
                <div style={{ display: 'block', padding: '0' }}>
                    <p style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '14px', color: '#1f2937' }}>
                        This is to certify that I have personally examined <strong>{certificate.patient_name}</strong> on 
                        <strong> {new Date(certificate.created_at).toLocaleDateString()}</strong>. 
                        The clinical findings and physical examination results are as follows:
                    </p>

                    {/* FINDINGS TABLE */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '11px' }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '22%', backgroundColor: '#f9fafb', fontWeight: '700', color: '#334155', border: '1px solid #e2e8f0', padding: '8px 10px' }}>Purpose</td>
                                <td style={{ border: '1px solid #e2e8f0', padding: '8px 10px', color: '#0f172a' }}>{certificate.purpose}</td>
                            </tr>
                            <tr>
                                <td style={{ backgroundColor: '#f9fafb', fontWeight: '700', color: '#334155', border: '1px solid #e2e8f0', padding: '8px 10px' }}>Vitals</td>
                                <td style={{ border: '1px solid #e2e8f0', padding: '8px 10px', color: '#0f172a' }}>
                                    BP: {certificate.blood_pressure || 'N/A'} mmHg | Pulse: {certificate.pulse || 'N/A'} bpm | Temp: {certificate.temperature || 'N/A'} {certificate.spo2 ? `| SpO2: ${certificate.spo2}%` : ''}

                                </td>
                            </tr>
                            <tr>
                                <td style={{ backgroundColor: '#f9fafb', fontWeight: '700', color: '#334155', border: '1px solid #e2e8f0', padding: '8px 10px' }}>Examination</td>
                                <td style={{ border: '1px solid #e2e8f0', padding: '8px 10px', color: '#0f172a' }}>
                                    <div style={{ marginBottom: '3px' }}><strong>General:</strong> {certificate.general_exam || 'Conscious, oriented, afebrile'}</div>
                                    <div style={{ marginBottom: '3px' }}><strong>Systemic:</strong> CVS: {certificate.cvs || 'Normal'} | RS: {certificate.rs || 'Normal'} | CNS: {certificate.cns || 'Normal'}</div>
                                    <div><strong>Skin:</strong> {certificate.skin || 'No contagious/infectious disease detected'}</div>
                                </td>
                            </tr>
                            {(findingsList.length > 0 || certificate.examination_findings) && (
                                <tr>
                                    <td style={{ backgroundColor: '#f9fafb', fontWeight: '700', color: '#334155', border: '1px solid #e2e8f0', padding: '8px 10px' }}>Findings</td>
                                    <td style={{ border: '1px solid #e2e8f0', padding: '8px 10px', color: '#0f172a' }}>
                                        {findingsList.map((item, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', marginBottom: '4px' }}>
                                                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#0ea5e9', marginTop: '5px', flexShrink: 0 }}></div>
                                                <span style={{ fontWeight: '500', lineHeight: '1.4' }}>{item}</span>
                                            </div>
                                        ))}
                                        {certificate.examination_findings && (
                                            <div style={{ marginTop: findingsList.length > 0 ? '4px' : 0 }}>
                                                {certificate.examination_findings}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* VERDICT SECTION */}
                    <div style={{ padding: '15px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', margin: '12px 0' }}>
                        <p style={{ margin: 0, fontSize: '13px', color: '#111827', lineHeight: '1.6' }}>
                            {certificate.verdict === 'Fit' ? (
                                <>
                                    The above-mentioned individual is <strong>medically fit</strong> to participate in 
                                    {certificate.purpose?.toLowerCase().includes('swimming') 
                                        ? ' swimming and pool-related activities.' 
                                        : ` ${certificate.purpose?.toLowerCase().replace('clearance', '').trim() || 'the activities mentioned above'}.`}
                                </>
                            ) : certificate.verdict === 'Unfit' ? (
                                <>
                                    The above-mentioned individual is <strong>medically unfit</strong> for 
                                    {certificate.purpose?.toLowerCase().replace('clearance', '').trim() || 'the specified purpose'}.
                                </>
                            ) : (
                                <strong>{certificate.verdict}</strong>
                            )}
                        </p>

                        {certificate.restrictions && (
                            <p style={{ color: '#b91c1c', fontWeight: '700', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
                                Note / Restrictions: {certificate.restrictions}
                            </p>
                        )}
                    </div>

                    <p style={{ fontSize: '11px', color: '#475569', marginBottom: '0', fontWeight: '600', textAlign: 'center' }}>
                        This certificate is valid for a period of 3 months from the date of issue.
                    </p>
                </div>

                {/* FOOTER - marginTop:auto pins it to bottom of the flex column */}
                <hr className="rx-divider rx-divider-footer" style={{ marginTop: 'auto' }} />

                <div className="rx-footer">
                    <div className="rx-footer-disclaimer">
                        <p style={{ fontWeight: '600', color: '#1e293b', marginBottom: '3px', fontSize: '10px' }}>
                            This certificate is issued based on clinical examination and patient history provided at the time of evaluation.
                        </p>
                        <p style={{ fontWeight: '600', color: '#64748b', margin: '0 0 8px 0', fontSize: '10px' }}>Not valid without signature.</p>
                        <p className="rx-powered-by" style={{ margin: 0 }}>Powered by <strong style={{ color: '#3b82f6', fontWeight: '800' }}>Klinik</strong></p>
                    </div>
                    <div className="rx-footer-signature">
                        {doctor.signature_url ? (
                            <img src={doctor.signature_url} alt="Doctor's Signature" className="rx-signature-img" />
                        ) : (
                            <div className="rx-no-signature" style={{ padding: '8px 16px', marginBottom: '4px' }}>Digitally Signed</div>
                        )}
                        <p className="rx-sig-name">Dr. {doctor.name}</p>
                        <p style={{ margin: 0, fontSize: '10px', color: '#475569', fontWeight: '500' }}>{doctor.qualification}</p>
                        <p style={{ margin: 0, fontSize: '10px', color: '#64748b' }}>Reg No: {doctor.registration_number}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CertificateViewer;
